/**
 * ConfirmPopup Component
 * 
 * Mobile-optimized confirmation dialog.
 * Renders as a bottom sheet on mobile, centered modal on desktop.
 * Supports "danger" and "default" variants.
 */

import { useEffect, useRef, useCallback } from 'react';
import { announceToScreenReader, trapFocus } from '../../utils/accessibility';
import './ConfirmPopup.css';

export interface ConfirmPopupProps {
  /** Whether the popup is visible */
  isOpen: boolean;
  /** Dialog title */
  title: string;
  /** Dialog message */
  message: string;
  /** Confirm button text */
  confirmText?: string;
  /** Cancel button text */
  cancelText?: string;
  /** Visual variant */
  variant?: 'default' | 'danger';
  /** Icon class (FontAwesome) */
  icon?: string;
  /** Callback when confirmed */
  onConfirm: () => void;
  /** Callback when cancelled */
  onCancel: () => void;
}

export function ConfirmPopup({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
  icon,
  onConfirm,
  onCancel,
}: ConfirmPopupProps) {
  const popupRef = useRef<HTMLDivElement>(null);

  const defaultIcon = variant === 'danger'
    ? 'fa-solid fa-triangle-exclamation'
    : 'fa-solid fa-circle-question';

  const iconClass = icon || defaultIcon;

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCancel();
    }
  }, [onCancel]);

  useEffect(() => {
    if (!isOpen) return;

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);
    announceToScreenReader(`${title}. ${message}`, 'assertive');

    const cleanup = popupRef.current ? trapFocus(popupRef.current) : undefined;

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
      cleanup?.();
    };
  }, [isOpen, handleKeyDown, title, message]);

  if (!isOpen) return null;

  return (
    <>
      <div className="confirm-popup-backdrop" onClick={onCancel} aria-hidden="true" />
      <div
        ref={popupRef}
        className={`confirm-popup confirm-popup--${variant}`}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-popup-title"
        aria-describedby="confirm-popup-message"
      >
        <div className="confirm-popup-icon">
          <div className="confirm-popup-icon-circle">
            <i className={iconClass} aria-hidden="true" />
          </div>
        </div>

        <div className="confirm-popup-content">
          <h2 id="confirm-popup-title" className="confirm-popup-title">{title}</h2>
          <p id="confirm-popup-message" className="confirm-popup-message">{message}</p>
        </div>

        <div className="confirm-popup-actions">
          <button
            className="confirm-popup-btn confirm-popup-btn-confirm"
            onClick={onConfirm}
            aria-label={confirmText}
          >
            {confirmText}
          </button>
          <button
            className="confirm-popup-btn confirm-popup-btn-cancel"
            onClick={onCancel}
            aria-label={cancelText}
          >
            {cancelText}
          </button>
        </div>

        <div className="confirm-popup-safe-area" />
      </div>
    </>
  );
}
